import { Injectable } from '@angular/core';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, toArray } from 'rxjs/operators';
import { ProjectService } from './project.service';

export interface HiddenEmployeeTarget {
  id?: number;
  name: string;
  email?: string;
}

export interface HiddenMigrationResult {
  id: number;
  name: string;
  email: string;
  password: string;
  success: boolean;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class HiddenToEmployeeControlledService {
  constructor(private projectService: ProjectService) {}

  migrateEmployees(
    employees: HiddenEmployeeTarget[]
  ): Observable<HiddenMigrationResult[]> {
    const targets = employees.filter((employee) => !!employee.id);
    if (!targets.length) {
      return of([]);
    }

    return this.projectService.getGlobalSettings().pipe(
      switchMap((rawGlobal: any) => {
        const globalSettings =
          rawGlobal && typeof rawGlobal === 'object'
            ? (rawGlobal.data ?? rawGlobal)
            : {};

        return this.disableGlobalHiddenMode(globalSettings).pipe(
          switchMap(() =>
            from(targets).pipe(
              concatMap((employee) =>
                this.migrateOne(employee, globalSettings).pipe(
                  catchError((error) =>
                    of({
                      id: employee.id as number,
                      name: employee.name,
                      email: employee.email || '',
                      password: '',
                      success: false,
                      error:
                        error?.error?.message ||
                        error?.message ||
                        'Migration failed',
                    } satisfies HiddenMigrationResult)
                  )
                )
              ),
              toArray()
            )
          )
        );
      }),
      map((results) => {
        if (results.some((result) => result.success)) {
          this.projectService.clearUserListCache();
        }
        return results;
      })
    );
  }

  private migrateOne(
    employee: HiddenEmployeeTarget,
    globalSettings: Record<string, unknown>
  ): Observable<HiddenMigrationResult> {
    const password = this.projectService.generatePassword();
    const email = employee.email || '';
    const settingsPayload = {
      ...globalSettings,
      ss_silent: 'false',
      ss_personalizeSettings: 'true',
      ss_periodic: String(globalSettings.ss_periodic ?? 'true'),
      ss_appchange: String(globalSettings.ss_appchange ?? 'false'),
      ss_webcam: String(globalSettings.ss_webcam ?? 'false'),
      ss_strict_time_tracking: String(globalSettings.ss_strict_time_tracking ?? 'false'),
      ss_automatic_tracking: String(globalSettings.ss_automatic_tracking ?? 'false'),
      ss_idle_time: String(globalSettings.ss_idle_time ?? '00:05:00'),
      ss_screen_capture_time: String(globalSettings.ss_screen_capture_time ?? '00:10:00'),
      ss_appchange_interval: String(globalSettings.ss_appchange_interval ?? '00:01:00'),
    };

    return forkJoin([
      this.projectService.resetUserPassword({
        id: employee.id,
        email,
        password,
      }),
      this.projectService.setGlobalSettings(settingsPayload, employee.id),
      this.projectService.updateEmployeeVersionType(employee.id as number, 'general'),
    ]).pipe(
      map(() => ({
        id: employee.id as number,
        name: employee.name,
        email,
        password,
        success: true,
      }))
    );
  }

  private disableGlobalHiddenMode(
    globalSettings: Record<string, unknown>
  ): Observable<unknown> {
    const silentEnabled = String(globalSettings?.ss_silent ?? '')
      .trim()
      .toLowerCase();
    if (silentEnabled !== 'true' && silentEnabled !== '1') {
      return of(null);
    }

    return this.projectService.setGlobalSettings(
      {
        ...globalSettings,
        ss_silent: 'false',
      },
      false
    );
  }
}
